import React from "react";
import { sendClientSideSplitTrackEvent } from "@leafly-com/split-next";
import { toKm } from "@leafly-com/web-utils";
import classnames from "classnames";
import Link from "next/link";
import { useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import {
  getCurrentDispensary,
  getPrimaryAddress,
} from "redux/selectors/dispensary";
import { getCoordinates } from "redux/selectors/location";
import { dispensaryType } from "utils/dispensaryUtils";
import { getStateOrProvinceLongName } from "utils/getStatesForCountry";
import isMobileDevice from "utils/isMobileDevice";
import { getSplitKey } from "utils/split/getSplitKey";

import Actions from "components/Dispensary/DispensaryHeader/Actions";
import BusinessHours from "components/Dispensary/DispensaryHeader/BusinessHours";
import PhotoGallery from "components/Dispensary/DispensaryHeader/PhotoGallery";
import ReviewRating from "components/Dispensary/DispensaryHeader/ReviewRating";
import DispensaryHeaderFulfillment from "components/Dispensary/DispensaryHeaderFulfillment";
import FollowButton from "components/Dispensary/FollowDispensaryButton";
import StorefrontIcon from "components/Icons/storefront.svg";
import Image from "components/Image";
import UberEgress from "components/UberEgress";

const DispensaryHeader: React.FC<{
  isInfoTier?: boolean;
  dispensaryHeaderDesignUpdates?: boolean;
}> = ({ isInfoTier = false, dispensaryHeaderDesignUpdates }) => {
  const countryCode = useDomainCountryCode();
  const {
    distanceMi,
    logoUrl,
    name,
    hasDeliveryEnabled,
    id,
    inStoreCartEnabled,
    pickupEnabled,
    city,
    state,
    retailType,
    tags,
    reviewCount,
    uberEatsStoreUrl,
  } = useSelector(getCurrentDispensary);
  const primaryAddress = useSelector(getPrimaryAddress);
  const coordinates = useSelector(getCoordinates);

  const distanceKm = toKm(distanceMi)?.toFixed(1);
  const localizedDistance =
    countryCode === "CA"
      ? `${distanceKm} km away`
      : `${distanceMi?.toFixed(1)} miles away`;
  const showDistance = !!distanceMi && (!hasDeliveryEnabled || pickupEnabled);

  const dispensaryTypeString = dispensaryType({
    dispensaryTags: tags,
    isCA: countryCode === "CA",
  });
  const isMobile = isMobileDevice();
  const { publishEvent } = useEventTracker();

  const handleSplitEventTracking = () => {
    sendClientSideSplitTrackEvent(
      {
        eventType: "NL-893_directionsLink_click",
        properties: { isMobile },
      },
      getSplitKey()
    );
  };

  return (
    <>
      <div className="container">
        {!isInfoTier && <PhotoGallery className="mb-lg" />}
        <div
          className={classnames("row justify-between", { "mt-6": isInfoTier })}
        >
          <div className="col lg:col-5 flex flex-col gap-4 relative">
            <FollowButton className="absolute top-0 right-0" />
            {isInfoTier ? (
              <div className="h-[50px] w-[50px] flex items-center justify-center bg-leafly-white rounded p-2">
                <StorefrontIcon className="opacity-20" width="30" height="30" />
              </div>
            ) : (
              <div className="overflow-hidden h-[50px] w-[50px] lg:h-[75px] lg:w-[75px]">
                <Image
                  src={logoUrl}
                  sizes={[50, 50, 50, 72, 72, 50]}
                  alt={name}
                  className="rounded"
                  data-testid="dispensary-header__logo"
                  disableLazyLoad
                />
              </div>
            )}
            <div className="flex items-start flex-col gap-1">
              <div className="flex">
                <div className="uppercase text-xs font-bold border border-light-grey rounded px-sm">
                  {retailType}
                </div>
                {dispensaryTypeString && (
                  <div className="pl-xs">
                    <div className="uppercase text-xs font-bold border border-light-grey rounded px-sm">
                      {dispensaryTypeString}
                    </div>
                  </div>
                )}
              </div>
              <h1 className="heading--m font-extrabold pr-xxl">{name}</h1>
              <div className="flex">
                <div className="text-xs pt-[1.25px]">
                  {dispensaryHeaderDesignUpdates
                    ? `${city}, ${state}`
                    : `${city}, ${getStateOrProvinceLongName(state)}`}
                </div>
                <span
                  className={`${
                    dispensaryHeaderDesignUpdates ? "mx-sm" : "mx-xs"
                  } ${reviewCount > 0 && "after:content-['•']"}`}
                />
                <ReviewRating
                  dispensaryHeaderDesignUpdates={dispensaryHeaderDesignUpdates}
                />
              </div>
              <div className="flex w-full justify-between items-center">
                {showDistance &&
                  (dispensaryHeaderDesignUpdates ? (
                    <div className="underline text-xs font-bold">
                      <Link
                        href={`https://www.google.com/maps/dir/${
                          coordinates.lat
                        },${coordinates.lon}/${encodeURIComponent(
                          primaryAddress
                        )}`}
                        onClick={() => {
                          handleSplitEventTracking();
                          publishEvent({
                            action: Action.click,
                            category: Category.dispensaryHeader,
                            dispensaryId: id,
                            label: "miles away get directions",
                          });
                        }}
                      >
                        {localizedDistance}
                      </Link>
                    </div>
                  ) : (
                    <div
                      className="text-xs"
                      data-testid="dispensary-header__distance"
                    >
                      {localizedDistance}
                    </div>
                  ))}
                <BusinessHours
                  dispensaryHeaderDesignUpdates={dispensaryHeaderDesignUpdates}
                />
              </div>
              {isMobile && isInfoTier && (
                <div
                  className="font-bold text-xs"
                  data-testid="claim-dispensary"
                >
                  <Link
                    href="https://success.leafly.com/claim-your-store"
                    className="underline text-green"
                    onClick={() =>
                      publishEvent({
                        action: Action.click,
                        category: Category.dispensaryMenu,
                        dispensaryId: id,
                        label: "info tier dispensary - upgrade now",
                      })
                    }
                  >
                    unclaimed business
                  </Link>
                </div>
              )}
            </div>
            <div>
              <Actions className="pb-lg" />
            </div>
          </div>
          <div className="col lg:col-5">
            <DispensaryHeaderFulfillment
              hideRequestOnlineOrdering={inStoreCartEnabled || isInfoTier}
            />
            {uberEatsStoreUrl && (
              <div className="hidden md:flex flex-col gap-md py-xs">
                <span className="flex self-center text-sm text-grey font-extrabold">
                  – OR –
                </span>
                <UberEgress url={uberEatsStoreUrl} dispensaryId={id} />
              </div>
            )}

            {!isMobile && isInfoTier && (
              <div
                className="p-lg border border-light-grey rounded"
                data-testid="claim-dispensary"
              >
                <p className="font-bold text-sm">Is this your business?</p>
                <p className="text-xs pr-4">
                  Level up to post deals, update your store info, upload your
                  menu, respond to reviews, and much more!
                </p>
                <Link
                  href="https://success.leafly.com/claim-your-store"
                  className="underline text-charcoal text-xs"
                  onClick={() =>
                    publishEvent({
                      action: Action.click,
                      category: Category.dispensaryMenu,
                      dispensaryId: id,
                      label: "info tier dispensary - upgrade now",
                    })
                  }
                >
                  claim now
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DispensaryHeader;
